import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AlertController, ToastController } from '@ionic/angular';
import {
    ParticipantEvaluatorsDict,
    ParticipantOverview,
    ParticipantRatingsDict
} from 'src/app/calibration/_interfaces/participant-overview.interface';
import { BaseClass } from 'src/app/shared/classes/base';
import { UserRoleEnum } from 'src/app/shared/enums/UserRoleEnum';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ConfigService } from 'src/app/shared/services/multi-clients/config.service';
import { TranslationService } from 'src/app/shared/services/translation.service';

@Component({
    selector: 'app-calibration-overall-evaluation',
    templateUrl: './overall-evaluation.component.html',
    styleUrls: ['./overall-evaluation.component.scss']
})
export class CalibrationUserOverallEvaluation extends BaseClass implements OnInit {

    @Input() participant: Partial<ParticipantOverview>;
    @Input() readonly meetingInProgress: boolean;
    @Input() readonly showTooltips: boolean = false;

    @Output() goToManagerEvaluation = new EventEmitter();

    public canEditManagerEvaluation = false;
    public selectedLanguage: string;
    public anyCalculated: boolean = false;
    public goalsCalculated: boolean = false;
    public principleCalculated: boolean = false;
    public finalCalculated: boolean = false;
    public useTranslationOnOverallEvaluation: boolean = false;
    public instructions = {
        relatedQuestionsUsed: 'manageVersion.calculate.instructions',
        formula: 'manageVersion.formula.instructions',
    }
    public showLeadershipValues: boolean = true;
    public isManager: boolean;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _alertController: AlertController,
        private _authService: AuthService,
        private _configService: ConfigService
    ) {
        super(_toastController, _translationService);
    }

    ngOnInit(): void {
        this.useTranslationOnOverallEvaluation = this._configService.settings.features.useTranslationOnOverallEvaluation;
        this.selectedLanguage = this._translationService.getSelectedLanguage();
        this.setSubscriptions();
        this.assignUserPermissions();
        this.computeEvaluationFlags();
    }

    private setSubscriptions() {
        this._authService.getIsManagerUser().subscribe((resp) => {
            this.isManager = resp.manager;
            this.assignUserPermissions();
        });
    }

    private assignUserPermissions() {
        //Dentro do card de calibragem, as seguintes funcionalidades deverão ser restritas:
        //Lápis (Editar Nota): Será exibido se o usuário tiver um dos papéis "BP", "Admin", "LM1" ou "LM2".
        const isUserAdmin = this._authService.hasRoleImpersonated(UserRoleEnum.Administrator);
        const isBpUser = this._authService.hasRoleImpersonated(UserRoleEnum.BusinessPartner);
        const isLineManager = this.participant && this.participant.user && this._authService.getLoggedUserId() === this.participant.user.lineManagerId;
        console.log('isUserAdmin', isUserAdmin, '/ isBpUser: ', isBpUser, '/ isLineManager: ', isLineManager, '/ this.isManager: ', this.isManager);


        this.canEditManagerEvaluation = isUserAdmin || isBpUser || isLineManager || this.isManager;
    }

    private computeEvaluationFlags() {
        this.anyCalculated =
            this.participant
            && (
                this.participant.selfEvaluation
                && (
                    this.participant.selfEvaluation.finalGoalsSelfEvaluationRelatedQuestionsUsed
                    || this.participant.selfEvaluation.finalPrinciplesSelfEvaluationRelatedQuestionsUsed
                    || this.participant.selfEvaluation.finalSelfEvaluationRelatedQuestionsUsed)
                || this.participant.managerEvaluation
                && (this.participant.managerEvaluation.finalGoalsManagerEvaluationRelatedQuestionsUsed
                    || this.participant.managerEvaluation.finalPrinciplesManagerEvaluationRelatedQuestionsUsed
                    || this.participant.managerEvaluation.finalManagerEvaluationRelatedQuestionsUsed)
            );
        this.goalsCalculated = (this.participant && this.participant.selfEvaluation && this.participant.selfEvaluation.finalGoalsSelfEvaluationRelatedQuestionsUsed)
            || (this.participant && this.participant.managerEvaluation && this.participant.managerEvaluation.finalGoalsManagerEvaluationRelatedQuestionsUsed)
            || false;
        this.principleCalculated = (this.participant && this.participant.selfEvaluation && this.participant.selfEvaluation.finalPrinciplesSelfEvaluationRelatedQuestionsUsed)
            || (this.participant && this.participant.managerEvaluation && this.participant.managerEvaluation.finalPrinciplesManagerEvaluationRelatedQuestionsUsed)
            || false;
        this.finalCalculated = (this.participant && this.participant.selfEvaluation && this.participant.selfEvaluation.finalSelfEvaluationRelatedQuestionsUsed)
            || (this.participant && this.participant.managerEvaluation && this.participant.managerEvaluation.finalManagerEvaluationRelatedQuestionsUsed)
            || false;

        this.canEditManagerEvaluation =
            (
                (this.participant
                    && this.participant.user
                    && this._authService.getLoggedUserId() === this.participant.user.lineManagerId
                )
                || this._authService.isInRole('BusinessPartner')
            );
    }


    public getTotalEvaluators(ratings: ParticipantRatingsDict): number {
        let total = 0;
        if (!ratings) return total;

        const keys = Object.keys(ratings);

        keys.forEach(key => {
            total = total + ratings[key];
        });

        return total;
    }

    public getAllEvaluators(ratings: ParticipantEvaluatorsDict): string {
        if (ratings) {
            let evaluators = '';
            if (!ratings) return evaluators;

            const keys = Object.keys(ratings);

            keys.forEach(key => {
                evaluators = evaluators + ratings[key];
            });

            return evaluators;
        }
        return null;
    }

    public getRatingTotals(rating: string): number {
        return (this.participant.ratings.freeEvaluationRatings ? this.participant.ratings.freeEvaluationRatings[rating] : null)
            + (this.participant.ratings.directReportRatings ? this.participant.ratings.directReportRatings[rating] : null)
            + (this.participant.ratings.threeSixtyRatings ? this.participant.ratings.threeSixtyRatings[rating] : null);
    }

    public getRatingTotalEvaluators(rating: string): string {
        return (this.participant.evaluators.freeEvaluators ? this.participant.evaluators.freeEvaluators[rating] : null)
            + (this.participant.evaluators.directReportEvaluators ? this.participant.evaluators.directReportEvaluators[rating] : null)
            + (this.participant.evaluators.threeSixtyEvaluators ? this.participant.evaluators.threeSixtyEvaluators[rating] : null);
    }

    public async confirmEditGrade(): Promise<void> {
        if (this.meetingInProgress) {
            this.goToManagerEvaluation.emit();
        } else {
            const alert = await this._alertController.create({
                message: this._translationService.getTranslation('calibration.confirmEditGrade'),
                buttons: [{
                    text: this._translationService.getTranslation('generic.cancel'),
                    role: 'cancel',
                    cssClass: 'danger'
                }, {
                    text: this._translationService.getTranslation('generic.editAnyway'),
                    handler: () => {
                        this.goToManagerEvaluation.emit();
                    }
                }]
            });

            await alert.present();
        }
    }

}
